import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Building2, Plus, Pencil, Trash2, Upload, Loader2, Mail, FileText, MapPin, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { createClientSupplierSchema } from '@timesheet/schemas';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { PageTransition } from '../components/PageTransition';
import { useMobile } from '../hooks/use-mobile';
import { PullToRefresh } from '../components/shared/pull-to-refresh';
import { BottomSheet } from '../components/shared/bottom-sheet';
import { clientSupplierService } from '../services/clientSupplierService';
import { useAuth, UserRole } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

enum ClientType {
  CLIENT = 'CLIENT',
  SUPPLIER = 'SUPPLIER',
  BOTH = 'BOTH',
}

interface ClientSupplier {
  id: number;
  name: string;
  type: ClientType;
  vatNumber: string;
  email: string;
  pec: string;
  address?: string;
  autoCreateTenant?: boolean;
}

interface AccessTenantOption {
  id: number;
  name: string;
  commercialEntityId?: number | null;
}

interface ImportPreviewRow {
  rowNumber: number;
  valid: boolean;
  error?: string;
  name?: string;
  type?: string;
  vatNumber?: string;
  email?: string;
  pec?: string;
  address?: string;
}

interface ImportPreviewResult {
  total: number;
  created: number;
  valid: number;
  errors: string[];
  rows: ImportPreviewRow[];
}

const initialFormData = {
  name: '',
  type: ClientType.CLIENT,
  vatNumber: '',
  email: '',
  pec: '',
  address: '',
  autoCreateTenant: false,
  accessTenantSelection: 'NEW',
};

const waitForUiPaint = (): Promise<void> => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => resolve());
    return;
  }
  setTimeout(resolve, 0);
});

const getTypeBadgeClass = (type: ClientType): string => {
  switch (type) {
    case ClientType.CLIENT:
      return 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700';
    case ClientType.SUPPLIER:
      return 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-700';
    case ClientType.BOTH:
      return 'bg-gradient-to-r from-emerald-100 to-violet-100 text-gray-700 border-gray-300 dark:from-emerald-900/30 dark:to-violet-900/30 dark:text-gray-300 dark:border-gray-600';
  }
};

const getClientTypeTranslationKey = (type: string): string => {
  switch (type) {
    case ClientType.CLIENT:
      return 'pages.clientsSuppliers.types.client';
    case ClientType.SUPPLIER:
      return 'pages.clientsSuppliers.types.supplier';
    case ClientType.BOTH:
      return 'pages.clientsSuppliers.types.both';
    default:
      return '';
  }
};

const ClientSupplierManager: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const [clientSuppliers, setClientSuppliers] = useState<ClientSupplier[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingClientSupplier, setEditingClientSupplier] = useState<ClientSupplier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [deletingClientSupplierId, setDeletingClientSupplierId] = useState<number | null>(null);
  const [importResult, setImportResult] = useState<{
    total: number;
    created: number;
    errors: string[];
  } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<ImportPreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogError, setImportDialogError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [invalidRowDetails, setInvalidRowDetails] = useState<ImportPreviewRow | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [accessTenants, setAccessTenants] = useState<AccessTenantOption[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isImportBusy = previewLoading || importing;

  const getClientTypeLabel = (type: string): string => {
    const key = getClientTypeTranslationKey(type);
    return key ? t(key) : type;
  };

  const fetchClientSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/client-supplier`);
      setClientSuppliers(response.data);
    } catch (err) {
      setError(t('pages.clientsSuppliers.errors.fetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchAccessTenants = useCallback(async () => {
    if (!isSuperAdmin) {
      setAccessTenants([]);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/tenants`);
      setAccessTenants(response.data);
    } catch {
      // Keep form usable even if tenant list fails to load
      setAccessTenants([]);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchClientSuppliers();
    fetchAccessTenants();
  }, [fetchClientSuppliers, fetchAccessTenants]);

  const handleOpen = () => {
    setOpen(true);
    setFormData(initialFormData);
    setEditingClientSupplier(null);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setEditingClientSupplier(null);
    setError(null);
    setFormErrors({});
  };

  const resetImportDialog = useCallback(() => {
    setSelectedImportFile(null);
    setImportPreview(null);
    setImportDialogError(null);
    setPreviewLoading(false);
    setImporting(false);
    setIsDraggingFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleOpenImport = () => {
    setImportOpen(true);
    resetImportDialog();
  };

  const handleCloseImport = () => {
    setImportOpen(false);
    resetImportDialog();
  };

  const previewImportFile = useCallback(async (file: File) => {
    setSelectedImportFile(file);
    setImportDialogError(null);
    setImportPreview(null);
    setPreviewLoading(true);

    // Let React paint the loading state before starting heavier work.
    await waitForUiPaint();

    try {
      const result = await clientSupplierService.previewImport(file);
      setImportPreview(result);
    } catch (err) {
      setImportDialogError(t('pages.clientsSuppliers.errors.import', 'Failed to import CSV file'));
    } finally {
      setPreviewLoading(false);
    }
  }, [t]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isImportBusy) return;
    const file = event.target.files?.[0];
    if (!file) return;
    await previewImportFile(file);
  };

  const handleImportConfirm = async () => {
    if (!selectedImportFile) return;

    try {
      setImporting(true);
      await waitForUiPaint();
      const result = await clientSupplierService.import(selectedImportFile);
      setImportResult(result);
      if (result.created > 0) {
        await fetchClientSuppliers();
      }
      handleCloseImport();
    } catch {
      setImportDialogError(t('pages.clientsSuppliers.errors.import', 'Failed to import CSV file'));
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await clientSupplierService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'client-supplier-import-template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setImportDialogError(t('pages.clientsSuppliers.errors.import', 'Failed to import CSV file'));
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    if (isImportBusy) return;
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await previewImportFile(file);
  };

  const getFriendlyRowError = (row: ImportPreviewRow): string => {
    if (!row.error) {
      return t('pages.clientsSuppliers.importErrorGeneric', 'Some fields are invalid. Check the row and try again.');
    }

    const mapImportErrorToFriendlyMessage = (error: string): string => {
      if (/^type:\s*Invalid enum value/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorTypeInvalid', 'Type is not valid. Use CLIENT, SUPPLIER, or BOTH.');
      }
      if (/^vatNumber:/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorVatRequired', 'VAT number is required.');
      }
      if (/^email:/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorEmailInvalid', 'Email is missing or invalid.');
      }
      if (/^pec:/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorPecInvalid', 'PEC is missing or invalid.');
      }
      if (/^name:/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorNameRequired', 'Name is required.');
      }
      if (/^duplicateName:/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorDuplicateName', 'Name is duplicated in the CSV or already exists.');
      }
      if (/^duplicateVatNumber:/i.test(error)) {
        return t('pages.clientsSuppliers.importErrorDuplicateVat', 'VAT number is duplicated in the CSV or already exists.');
      }
      return t('pages.clientsSuppliers.importErrorGeneric', 'Some fields are invalid. Check the row and try again.');
    };

    return row.error
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => mapImportErrorToFriendlyMessage(part))
      .join(' ');
  };

  const handleEdit = (clientSupplier: ClientSupplier) => {
    const linkedTenant = accessTenants.find((tenant) => tenant.commercialEntityId === clientSupplier.id);

    setEditingClientSupplier(clientSupplier);
    setFormData({
      name: clientSupplier.name,
      type: clientSupplier.type,
      vatNumber: clientSupplier.vatNumber,
      email: clientSupplier.email,
      pec: clientSupplier.pec,
      address: clientSupplier.address || '',
      autoCreateTenant: Boolean(linkedTenant) || clientSupplier.autoCreateTenant || false,
      accessTenantSelection: linkedTenant ? String(linkedTenant.id) : 'NEW',
    });
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    setDeleteTargetId(id);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId === null) return;

    try {
      setDeletingClientSupplierId(deleteTargetId);
      await axios.delete(`${API_URL}/client-supplier/${deleteTargetId}`);
      await fetchClientSuppliers();
      setDeleteTargetId(null);
    } catch (err) {
      // Toast shown automatically by global interceptor
    } finally {
      setDeletingClientSupplierId(null);
    }
  };

  const extractBackendValidationErrors = (data: unknown): Record<string, string> => {
    const parsedErrors: Record<string, string> = {};

    if (!data || typeof data !== 'object') {
      return { _server: t('pages.clientsSuppliers.errors.validation', 'Validation failed') };
    }

    const payload = data as {
      message?: unknown;
      errors?: Array<{ path?: unknown; message?: unknown }>;
      issues?: Array<{ path?: unknown; message?: unknown }>;
      error?: unknown;
    };

    const addIssueMessage = (field: string, message: string) => {
      const normalizedField = field || '_server';
      if (!parsedErrors[normalizedField]) {
        parsedErrors[normalizedField] = message;
      }
    };

    const parseIssueArray = (issueList: Array<{ path?: unknown; message?: unknown }>) => {
      issueList.forEach((issue) => {
        const issueMessage = typeof issue?.message === 'string' ? issue.message.trim() : '';
        if (!issueMessage) return;

        let field = '_server';
        if (Array.isArray(issue?.path) && issue.path.length > 0) {
          const pathParts = issue.path.map((part) => String(part));
          field = pathParts[pathParts.length - 1] || '_server';
        }

        addIssueMessage(field, issueMessage);
      });
    };

    if (Array.isArray(payload.errors)) {
      parseIssueArray(payload.errors);
    }

    if (Array.isArray(payload.issues)) {
      parseIssueArray(payload.issues);
    }

    if (Array.isArray(payload.message)) {
      const textMessages = payload.message.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
      if (textMessages.length > 0) {
        addIssueMessage('_server', textMessages.join(', '));
      }
    } else if (typeof payload.message === 'string' && payload.message.trim().length > 0) {
      const hasSpecificErrors = Object.keys(parsedErrors).some((key) => key !== '_server');
      if (!(payload.message === 'Validation failed' && hasSpecificErrors)) {
        addIssueMessage('_server', payload.message.trim());
      }
    }

    if (!parsedErrors._server && typeof payload.error === 'string' && payload.error.trim().length > 0) {
      const genericErrors = new Set(['Bad Request', 'Validation failed']);
      if (!genericErrors.has(payload.error.trim())) {
        addIssueMessage('_server', payload.error.trim());
      }
    }

    if (Object.keys(parsedErrors).length === 0) {
      parsedErrors._server = t('pages.clientsSuppliers.errors.validation', 'Validation failed');
    }

    return parsedErrors;
  };

  const handleSubmit = async () => {
    try {
      const { accessTenantSelection, ...formPayload } = formData;
      const payload = editingClientSupplier
        ? {
            ...formPayload,
            autoCreateTenant: undefined,
            linkedTenantId: undefined,
          }
        : {
            ...formPayload,
            linkedTenantId: formData.autoCreateTenant && accessTenantSelection !== 'NEW'
              ? Number(accessTenantSelection)
              : undefined,
          };

      // Validate form data client-side
      const validationSchema = editingClientSupplier
        ? createClientSupplierSchema.partial()
        : createClientSupplierSchema;
      
      const validation = validationSchema.safeParse(payload);
      
      if (!validation.success) {
        // Build errors map from Zod validation
        const errors: Record<string, string> = {};
        validation.error.errors.forEach((err) => {
          const field = err.path.join('.');
          errors[field] = err.message;
        });
        setFormErrors(errors);
        return;
      }
      
      setFormErrors({});
      setSaving(true);
      
      if (editingClientSupplier) {
        await axios.patch(
          `${API_URL}/client-supplier/${editingClientSupplier.id}`,
          payload
        );
      } else {
        await axios.post(`${API_URL}/client-supplier`, payload);
      }
      handleClose();
      await fetchClientSuppliers();
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setFormErrors(extractBackendValidationErrors(err.response?.data));
      } else {
        setFormErrors({
          _server: t('pages.clientsSuppliers.errors.validation', 'Validation failed'),
        });
      }
      // Toast shown automatically by global interceptor
    } finally {
      setSaving(false);
    }
  };

  const selectableAccessTenants = accessTenants.filter((tenant) => {
    if (!tenant.commercialEntityId) {
      return true;
    }

    return editingClientSupplier ? tenant.commercialEntityId === editingClientSupplier.id : false;
  });

  const renderClientSupplierCards = () => {
    if (clientSuppliers.length === 0 && !loading) {
      return (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('pages.clientsSuppliers.noResults')}
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {clientSuppliers.map((cs) => (
          <Card key={cs.id}>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    cs.type === ClientType.CLIENT
                      ? 'bg-emerald-100 dark:bg-emerald-900/30'
                      : cs.type === ClientType.SUPPLIER
                        ? 'bg-violet-100 dark:bg-violet-900/30'
                        : 'bg-gradient-to-r from-emerald-100 to-violet-100 dark:from-emerald-900/30 dark:to-violet-900/30'
                  }`}>
                    <Building2 className={`h-5 w-5 ${
                      cs.type === ClientType.CLIENT
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : cs.type === ClientType.SUPPLIER
                          ? 'text-violet-600 dark:text-violet-400'
                          : 'text-gray-600 dark:text-gray-400'
                    }`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold truncate">{cs.name || '-'}</p>
                      <Badge variant="outline" className={`${getTypeBadgeClass(cs.type)} text-xs`}>
                        {getClientTypeLabel(cs.type)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono mt-1">{cs.vatNumber || '-'}</p>
                  </div>
                </div>

                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <p className="flex items-center gap-1 truncate">
                    <Mail className="h-3 w-3" />
                    {cs.email || '-'}
                  </p>
                  <p className="flex items-center gap-1 truncate">
                    <FileText className="h-3 w-3" />
                    {cs.pec || '-'}
                  </p>
                  <p className="flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3" />
                    {cs.address || '-'}
                  </p>
                </div>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(cs)} className="gap-1">
                    <Pencil className="h-4 w-4" />
                    {t('pages.clientsSuppliers.actions.edit')}
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(cs.id)} className="gap-1">
                    <Trash2 className="h-4 w-4" />
                    {t('common.delete', 'Delete')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <PageTransition>
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('pages.clientsSuppliers.title')}</h1>

        <BottomSheet
          open={deleteTargetId !== null}
          onClose={() => setDeleteTargetId(null)}
          title={t('pages.clientsSuppliers.confirmDeleteTitle', 'Delete client/supplier')}
        >
          <p className="mb-6 text-sm text-muted-foreground">{t('pages.clientsSuppliers.confirmDelete')}</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteTargetId(null)} disabled={deletingClientSupplierId !== null}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deletingClientSupplierId !== null}>
              {deletingClientSupplierId !== null ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('common.delete', 'Delete')}
            </Button>
          </div>
        </BottomSheet>
            <p className="text-sm text-muted-foreground">
              {t('pages.clientsSuppliers.subtitle', 'Manage clients and suppliers')}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto sm:flex-nowrap">
          <Button
            variant="outline"
            onClick={handleOpenImport}
            className="flex-1 gap-2 sm:flex-none"
          >
            <Upload className="h-4 w-4" />
            {t('pages.clientsSuppliers.actions.import')}
          </Button>
          <Button onClick={handleOpen} className="flex-1 bg-emerald-500 hover:bg-emerald-600 gap-2 sm:flex-none">
            <Plus className="h-4 w-4" />
            {t('pages.clientsSuppliers.actions.create')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {importResult && (
        <Alert variant={importResult.errors.length > 0 ? 'warning' : 'success'}>
          <div className="space-y-1">
            <p className="font-medium">{t('pages.clientsSuppliers.importResults.title')}:</p>
            <p>
              {t('pages.clientsSuppliers.importResults.total')}: {importResult.total},{' '}
              {t('pages.clientsSuppliers.importResults.created')}: {importResult.created}
            </p>
            {importResult.errors.length > 0 && (
              <ul className="list-disc list-inside text-sm">
                {importResult.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        </Alert>
      )}

      {/* Content with Pull-to-Refresh */}
      <PullToRefresh onRefresh={fetchClientSuppliers} disabled={!isMobile}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : (
          renderClientSupplierCards()
        )}
      </PullToRefresh>

      {/* Create/Edit Form - BottomSheet on mobile, Modal on desktop */}
      <BottomSheet
        open={importOpen}
        onClose={handleCloseImport}
        size="wide"
        title={t('pages.clientsSuppliers.actions.import')}
        description={t('pages.clientsSuppliers.importDescription')}
      >
        <div className="relative space-y-4">
          {isImportBusy && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-[1px]">
              <div className="inline-flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {previewLoading
                  ? t('pages.clientsSuppliers.previewing')
                  : t('pages.clientsSuppliers.importing')}
              </div>
            </div>
          )}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileUpload}
          />

          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex justify-center">
              <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
                <Download className="h-4 w-4" />
                {t('pages.clientsSuppliers.downloadTemplate')}
              </Button>
            </div>

            <div className="text-sm text-muted-foreground text-center space-y-1">
              <p>{t('pages.clientsSuppliers.templateHint')}</p>
            </div>
          </div>

          <div
            onDragOver={(event) => {
              event.preventDefault();
              setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleDrop}
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${isDraggingFile ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10' : 'border-muted-foreground/25'}`}
          >
            <Upload className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">{t('pages.clientsSuppliers.dropzoneTitle')}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedImportFile
                ? t('pages.clientsSuppliers.selectedFile', { name: selectedImportFile.name })
                : t('pages.clientsSuppliers.dropzoneDescription')}
            </p>
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2" disabled={isImportBusy}>
                <Upload className="h-4 w-4" />
                {t('pages.clientsSuppliers.selectFile')}
              </Button>
            </div>
          </div>

          {importDialogError && <Alert variant="error">{importDialogError}</Alert>}

          {previewLoading && (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('pages.clientsSuppliers.previewing')}
            </div>
          )}

          {importPreview && !previewLoading && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.clientsSuppliers.importResults.total')}</p>
                    <p className="text-2xl font-semibold">{importPreview.total}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.clientsSuppliers.validRows')}</p>
                    <p className="text-2xl font-semibold text-emerald-600">{importPreview.valid}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pages.clientsSuppliers.invalidRows')}</p>
                    <p className="text-2xl font-semibold text-amber-600">{importPreview.errors.length}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>{t('pages.clientsSuppliers.columns.name')}</TableHead>
                      <TableHead>{t('pages.clientsSuppliers.columns.type')}</TableHead>
                      <TableHead>{t('pages.clientsSuppliers.columns.vatNumber')}</TableHead>
                      <TableHead>{t('pages.clientsSuppliers.columns.email')}</TableHead>
                      <TableHead>{t('common.status', 'Status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importPreview.rows.map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.name || '-'}</TableCell>
                        <TableCell>{row.type ? getClientTypeLabel(row.type) : '-'}</TableCell>
                        <TableCell className="font-mono text-xs">{row.vatNumber || '-'}</TableCell>
                        <TableCell className="text-xs">{row.email || '-'}</TableCell>
                        <TableCell>
                          {row.valid ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-sm">
                              <CheckCircle2 className="h-4 w-4" />
                              {t('pages.clientsSuppliers.rowValid')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setInvalidRowDetails(row)}
                              className="inline-flex items-center gap-1 text-amber-600 text-sm underline-offset-2 hover:underline"
                            >
                              <AlertTriangle className="h-4 w-4" />
                              {t('pages.clientsSuppliers.rowInvalid')}
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {importPreview.errors.length === 0 && importPreview.total > importPreview.rows.length && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                          {t('pages.clientsSuppliers.previewContinues')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleCloseImport} disabled={importing} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleImportConfirm}
              disabled={!selectedImportFile || !importPreview || importPreview.valid === 0 || importing}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('pages.clientsSuppliers.importing')}
                </>
              ) : (
                t('pages.clientsSuppliers.confirmImport')
              )}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        open={Boolean(invalidRowDetails)}
        onClose={() => setInvalidRowDetails(null)}
        title={t('pages.clientsSuppliers.invalidRowDialogTitle')}
        description={invalidRowDetails ? t('pages.clientsSuppliers.invalidRowDialogDescription', { row: invalidRowDetails.rowNumber }) : undefined}
      >
        <div className="space-y-4">
          {invalidRowDetails && (
            <Alert variant="warning">
              {getFriendlyRowError(invalidRowDetails)}
            </Alert>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setInvalidRowDetails(null)}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        open={open}
        onClose={handleClose}
        title={
          editingClientSupplier
            ? t('pages.clientsSuppliers.actions.edit')
            : t('pages.clientsSuppliers.actions.create')
        }
        description={
          editingClientSupplier
            ? t('pages.clientsSuppliers.editDescription', 'Update client/supplier information')
            : t('pages.clientsSuppliers.createDescription', 'Add a new client or supplier')
        }
      >
        <div className="space-y-4">
          {formErrors._server && (
            <Alert variant="warning">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                <span>{formErrors._server}</span>
              </div>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">{t('pages.clientsSuppliers.columns.name')}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('pages.clientsSuppliers.namePlaceholder', 'Company Name')}
              className={formErrors.name ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {formErrors.name && (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {formErrors.name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('pages.clientsSuppliers.columns.type')}</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value as ClientType })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(ClientType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {getClientTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vatNumber">{t('pages.clientsSuppliers.columns.vatNumber')}</Label>
            <Input
              id="vatNumber"
              value={formData.vatNumber}
              onChange={(e) => setFormData({ ...formData, vatNumber: e.target.value })}
              placeholder="IT12345678901"
              className={formErrors.vatNumber ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {formErrors.vatNumber && (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {formErrors.vatNumber}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('pages.clientsSuppliers.columns.email')}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="contact@company.com"
              className={formErrors.email ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {formErrors.email && (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {formErrors.email}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="pec">{t('pages.clientsSuppliers.columns.pec')}</Label>
            <Input
              id="pec"
              type="email"
              value={formData.pec}
              onChange={(e) => setFormData({ ...formData, pec: e.target.value })}
              placeholder="company@pec.it"
              className={formErrors.pec ? 'border-red-500 focus:ring-red-500' : ''}
            />
            {formErrors.pec && (
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {formErrors.pec}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t('pages.clientsSuppliers.columns.address', 'Address')}</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder={t('pages.clientsSuppliers.addressPlaceholder', 'Street, City, ZIP')}
            />
          </div>

          {isSuperAdmin && !editingClientSupplier && (
          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="autoCreateTenant" className="font-medium">
                {t('pages.clientsSuppliers.form.autoCreateTenant', 'Create Azienda Accesso')}
              </Label>
              <Switch
                id="autoCreateTenant"
                checked={formData.autoCreateTenant}
                onCheckedChange={(checked) => setFormData({
                  ...formData,
                  autoCreateTenant: Boolean(checked),
                })}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('pages.clientsSuppliers.form.autoCreateTenantHint', 'Automatically create a matched company in Aziende Accesso.')}
            </p>

            <div className={`space-y-2 ${formData.autoCreateTenant ? '' : 'opacity-60'}`}>
              <Label>{t('pages.clientsSuppliers.form.accessTenantTarget', 'Azienda Accesso target')}</Label>
              <Select
                value={formData.accessTenantSelection}
                onValueChange={(value) => setFormData({
                  ...formData,
                  accessTenantSelection: value,
                })}
                disabled={!formData.autoCreateTenant}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pages.clientsSuppliers.form.selectAccessTenantTarget', 'Select target company')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">
                    {t('pages.clientsSuppliers.form.accessTenantTargetNew', 'Create new company')}
                  </SelectItem>
                  {selectableAccessTenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={String(tenant.id)}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('pages.clientsSuppliers.form.accessTenantTargetHint', 'Choose whether to link to an existing company or create a new one.')}
              </p>
            </div>

          </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={saving} className="flex-1">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving', 'Saving...')}
                </>
              ) : editingClientSupplier ? (
                t('common.save')
              ) : (
                t('common.create')
              )}
            </Button>
          </div>
        </div>
      </BottomSheet>
    </div>
    </PageTransition>
  );
};

export default ClientSupplierManager;
